import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMembers } from '@/hooks/use-members';
import { useStartChat } from '@/hooks/use-start-chat';
import { User, Search, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { Badge } from '@/components/ui/badge';

interface NewMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewMessageDialog({ open, onOpenChange }: NewMessageDialogProps) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useMembers({ page: 1, pageSize: 50, search });
  const { startChat } = useStartChat();
  const { user } = useAuth();

  const handleSelectMember = async (memberId: string, memberName: string) => {
    const success = await startChat(memberId, memberName);
    if (success) {
      onOpenChange(false);
      setSearch('');
    }
  };

  const members = data?.rows || [];
  const filteredMembers = members.filter(m => m.user_id !== user?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Message</DialogTitle>
          <DialogDescription>
            Search for a member to start a conversation
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 max-w-sm"
              autoFocus
            />
          </div>

          <ScrollArea className="h-[300px] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {search ? 'No members found' : 'Start typing to search members'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredMembers.map((member) => {
                  const fullName = `${member.first_name} ${member.last_name || ''}`.trim();
                  return (
                    <Button
                      key={member.user_id}
                      variant="ghost"
                      className="w-full justify-start gap-3 h-auto py-3"
                      onClick={() => handleSelectMember(member.user_id, fullName)}
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-medium truncate flex items-center gap-2 max-w-xs">
                          {fullName}
                          {member.isAdmin && (
                            <Badge variant="secondary" className="text-xs">
                              Admin
                            </Badge>
                          )}
                        </div>
                        {member.email && (
                          <div className="text-sm text-muted-foreground truncate max-w-xs">
                            {member.email}
                          </div>
                        )}
                      </div>
                    </Button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
